import { FC, useState } from "react";
import { motion } from "framer-motion";
import {
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon,
  UserIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

interface ModernReservationRequestCardProps {
  reservation: any;
  onView: (reservation: any) => void;
  onApprove: (reservation: any) => void;
  onReject: (reservation: any) => void;
  isLoadingApprove?: boolean;
  isLoadingReject?: boolean;
  isHidden?: boolean;
  isEventPassed?: boolean;
}

const ModernReservationRequestCard: FC<ModernReservationRequestCardProps> = ({
  reservation,
  onView,
  onApprove,
  onReject,
  isLoadingApprove = false,
  isLoadingReject = false,
  isHidden = false,
  isEventPassed = false,
}) => {
  const [imageError, setImageError] = useState(false);

  if (isHidden) return null;

  const canTakeAction = reservation?.approuved == null && !isEventPassed;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      <div className="relative">
        {/* Image Section */}
        <div className="aspect-[4/3] relative overflow-hidden bg-gray-100 dark:bg-gray-700">
          {!imageError && reservation?.Location?.images?.[0] ? (
            <img
              src={`${process.env.REACT_APP_CLOUD_FRONT}${reservation.Location.images[0]}`}
              alt={reservation?.Location?.title || "Espace"}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <MapPinIcon className="w-12 h-12 text-gray-400" />
            </div>
          )}

          {/* Status Badge */}
          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
              <ClockIcon className="w-4 h-4 mr-1" />
              En attente
            </span>
          </div>

          {/* Event Passed Warning */}
          {isEventPassed && (
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                Événement passé
              </span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {reservation?.Location?.title || "Espace"}
              </h3>
              <div className="flex items-center text-gray-600 dark:text-gray-300 mb-2">
                <UserIcon className="w-4 h-4 mr-2" />
                <span className="text-sm">Réf: {reservation?.reference}</span>
              </div>
              {reservation?.Location?.city && (
                <div className="flex items-center text-gray-600 dark:text-gray-300">
                  <MapPinIcon className="w-4 h-4 mr-2" />
                  <span className="text-sm">{reservation.Location.city}</span>
                </div>
              )}
            </div>

            {/* View Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onView(reservation)}
              className="p-2 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <EyeIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </motion.button>
          </div>

          {/* Action Buttons */}
          {canTakeAction && (
            <div className="flex items-center space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              {isEventPassed ? (
                <div className="flex-1 text-center">
                  <div className="relative group">
                    <button
                      disabled
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-400 rounded-xl cursor-not-allowed flex items-center justify-center"
                    >
                      <CheckCircleIcon className="w-5 h-5 mr-2" />
                      Trop tard pour accepter
                    </button>

                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      L'événement est déjà passé
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Accept Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onApprove(reservation)}
                    disabled={isLoadingApprove || isLoadingReject}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-medium flex items-center justify-center"
                  >
                    {isLoadingApprove ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <CheckCircleIcon className="w-5 h-5 mr-2" />
                    )}
                    Accepter
                  </motion.button>

                  {/* Reject Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onReject(reservation)}
                    disabled={isLoadingApprove || isLoadingReject}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-medium flex items-center justify-center"
                  >
                    {isLoadingReject ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <XCircleIcon className="w-5 h-5 mr-2" />
                    )}
                    Refuser
                  </motion.button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ModernReservationRequestCard;
